import { capitalize } from 'lodash'

import { isKubernetesStage } from 'components/attacks/utils/kubernetes'
import theme from 'theme'

export const STAGES = {
  NOT_STARTED: ['NotStarted', 'scheduled'],
  IN_FLIGHT: ['Distributed', 'Pending', 'Initializing', 'Running', 'Active'],
  SUCCESSFUL: ['Successful'],
  HALTING: [
    'Halt',
    'HaltDistributed',
    'RollbackTriggered',
    'InterruptTriggered',
    'HaltStarted',
    'RollbackStarted',
    'HaltRequested'
  ],
  FAILED: [
    'Failed',
    'ClientAborted',
    'HaltFailed',
    'InitializationFailed',
    'ERROR'
  ],
  UNSUCCESSFUL: ['Aborted', 'TargetNotFound', 'LostCommunication', 'UserHalted']
}

export const dotColors = {
  blue: theme.colors.blue[0],
  green: theme.colors.green[0],
  orange: theme.colors.orange[0],
  red: theme.colors.red[0],
  steel: theme.colors.steel[1],
  yellow: theme.colors.yellow[0]
} as const

const DOT_COLOR = {
  IN_FLIGHT: dotColors.blue,
  SUCCESSFUL: dotColors.green,
  HALTING: dotColors.yellow,
  FAILED: dotColors.red,
  UNSUCCESSFUL: dotColors.steel
} as const

const findStageType = (status) =>
  Object.keys(DOT_COLOR).find((key) =>
    STAGES[key].some((colorState) => colorState === status)
  )

//Active refers to things still moving, most relevent to polling.
export const ACTIVE_STAGES = [...STAGES.IN_FLIGHT, ...STAGES.HALTING]

export const FINISHED_STAGES = [
  ...STAGES.SUCCESSFUL,
  ...STAGES.FAILED,
  ...STAGES.UNSUCCESSFUL
]

// Note: in this case, "finished" means that the k8s attack data is resolved and will not update
// this indicates to us that we should not poll for updates after one of these stages is reached
// it is important to not that this does not necessarily mean that the attack is complete
//
// ERROR means that the attack encountered an error and is completed
//
// UserHalted means that the user halted the attack and it is completed
//            Yes, this casing is correct. The "USER_HALTED" stage is intentionally serialized like this on the backend.
//            see: https://github.com/gremlin/service/blob/staging/libraries/core/src/main/java/com/gremlininc/model/k8s/K8sAttack.java#L319
//
// LAUNCHED means that the k8s portion of the attack has resolved and the container attack was started
//          it DOES NOT mean that the attack has completed
export const KUBERNETES_RESOLVED_STAGES = ['LAUNCHED', 'ERROR', 'UserHalted']

// Stage
export const getAttackStageColor = (stage: string) =>
  DOT_COLOR[findStageType(stage)]
export const isInCompleteStage = (stage: string) =>
  FINISHED_STAGES.includes(stage)
export const isInActiveStage = (stage: string) => ACTIVE_STAGES.includes(stage)
export const isInFlightState = (stage: string) =>
  STAGES.IN_FLIGHT.includes(stage)
export const isInNotStartedStage = (stage: string) =>
  STAGES.NOT_STARTED.includes(stage)
export const isInHaltingStage = (stage: string) =>
  STAGES.HALTING.includes(stage)
export const isInFailedStage = (stage: string) => STAGES.FAILED.includes(stage)
export const isInSuccessfulStage = (stage: string) =>
  STAGES.SUCCESSFUL.includes(stage)

// Attack
export const isAttackInCompleteState = (attack) =>
  isInCompleteStage(attack?.stage)
export const isAttackInActiveState = (attack) => isInActiveStage(attack?.stage)
export const isKubernetesAttackInResolvedState = (attack) =>
  KUBERNETES_RESOLVED_STAGES.includes(attack?.stage)

export function getAttackStageReadable(stage = '', error = '') {
  const formattedStage = isKubernetesStage(stage)
    ? capitalize(stage)
    : stage.replace(/([A-Z])/g, ' $1').trim()

  return `${formattedStage}${error}`
}
